import { useMemo } from 'react';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { MarketplaceIds } from '../../../../enums/marketplace-ids';
import {
  useEntitiesFulfillmentPlanProperty,
  useEntitiesFulfillmentPlansFromFulfillmentPlanIds,
} from '../../../../hooks/entities/selectors/fulfillment-plans';
import { useEntitiesSalesOrders } from '../../../../hooks/entities/selectors/sales-orders';

export const UnlinkedFulfillmentPlanItemKey = 'unlinked';

const addItemToAcc = (acc, key, itemToAdd) => {
  return {
    ...acc,
    [key]: [...(acc[key] ? acc[key] : []), itemToAdd],
  };
};

const useEditItemsData = (fulfillmentPlanId) => {
  const salesOrdersFulfilledIds = useEntitiesFulfillmentPlanProperty(fulfillmentPlanId, 'salesOrdersFulfilledIds');
  const salesOrders = useEntitiesSalesOrders(salesOrdersFulfilledIds) || emptyArray;

  const allFulfillmentPlanIds = useMemo(() => {
    const allIds = salesOrders.flatMap((salesOrder) => salesOrder.fulfillmentPlanIds);
    return [...new Set(allIds)];
  }, [salesOrders]);

  const fulfillmentPlans = useEntitiesFulfillmentPlansFromFulfillmentPlanIds(allFulfillmentPlanIds) || emptyArray;

  const items = useMemo(() => {
    const salesOrderItemIdToSalesOrderIdMap = salesOrders
      .flatMap((salesOrder) => {
        return salesOrder.items.map((item) => ({
          [item.salesOrderItemId]: salesOrder.salesOrderId,
        }));
      })
      .reduce((acc, itemMap) => {
        return {
          ...acc,
          ...itemMap,
        };
      }, {});

    const splitItems = Object.entries(
      fulfillmentPlans
        .flatMap((fp) => fp.items)
        .reduce((acc, item) => {
          const mergeKey = item.salesOrderItemId || UnlinkedFulfillmentPlanItemKey;
          return addItemToAcc(acc, mergeKey, item);
        }, {}),
    )
      .filter(([, value]) => value.length > 1)
      .map(([key]) => key);

    const currentFulfillmentPlan =
      fulfillmentPlans.find((fulfillmentPlan) => fulfillmentPlan.fulfillmentPlanId === fulfillmentPlanId) || emptyObject;

    return (
      currentFulfillmentPlan.items?.map((item) => {
        const linkedSalesOrderId = salesOrderItemIdToSalesOrderIdMap[item.salesOrderItemId];
        const linkedSalesOrder = salesOrders.find((salesOrder) => salesOrder.salesOrderId === linkedSalesOrderId);
        if (!linkedSalesOrder) {
          return {
            ...item,
            fulfillmentPlanId,
            isLinkedToSplitSalesOrderItem: false,
            isLinkedToMarketplaceOrder: false,
            isUnlinked: true,
          };
        }

        const isLinkedToSplitSalesOrderItem = splitItems.includes(item.salesOrderItemId);
        const isLinkedToMarketplaceOrder = linkedSalesOrder.store.marketplaceId !== `${MarketplaceIds.ShipStation}`;
        return {
          ...item,
          salesOrderId: linkedSalesOrder.salesOrderId,
          fulfillmentPlanId,
          isLinkedToSplitSalesOrderItem,
          isLinkedToMarketplaceOrder,
          isUnlinked: false,
        };
      }) || emptyArray
    );
  }, [fulfillmentPlans, salesOrders, fulfillmentPlanId]);

  return {
    items,
  };
};

export default useEditItemsData;
